import { useState } from 'react';
import { ECustomFieldType } from 'src/app/project/modules/custom-fields/custom-field-types-enums';
import {
  EIntegrationStatus,
  TCustomField,
} from 'src/app/project/modules/custom-fields/custom-fields.model';
import { TFieldExclusionReason } from '../molecules/ImportCustomFieldRow';
import SelectImportGroups from '../molecules/SelectImportGroups';
import SelectImportSite, { TSelectOption, TSiteOption } from '../molecules/SelectImportSite';
import { isGroup, TLayoutEntry, TLayoutGroup } from './CustomFieldGroupsSettings';

export type TExcludedFields = Record<string, TFieldExclusionReason>;

const hasFields = (entry: TLayoutEntry): boolean =>
  isGroup(entry) ? entry.items.length > 0 : true;

const getExclusionReason = (
  field: TCustomField,
  existingCustomFieldLabels: string[],
): TFieldExclusionReason | undefined => {
  if (field.volyIntegrationActive !== EIntegrationStatus.OFF) {
    return 'VOLY_INTEGRATION';
  }

  if (existingCustomFieldLabels.includes(field.label)) {
    return 'NAME_TAKEN';
  }

  return undefined;
};

const getExcludedFields = (
  fields: TCustomField[],
  existingCustomFieldLabels: string[],
): TExcludedFields =>
  fields.reduce((excluded: TExcludedFields, field) => {
    const reason = getExclusionReason(field, existingCustomFieldLabels);

    if (!reason) {
      return excluded;
    }

    return { ...excluded, [field.id]: reason };
  }, {});

const getSelectableFieldIds = (fields: TCustomField[], excludedFields: TExcludedFields): string[] =>
  fields.filter((field) => !excludedFields[field.id]).map((field) => field.id);

export type TTargetCustomField = { label: string; type: ECustomFieldType };

const hasExcludedSelection = (
  entry: TLayoutEntry,
  selectableFieldIds: string[],
  selectedFieldIds: string[],
): boolean => {
  if (!isGroup(entry)) {
    return false;
  }

  const someSelected = entry.items.some((field) => selectedFieldIds.includes(field.id));
  const someExcluded = entry.items.some((field) => !selectableFieldIds.includes(field.id));

  return someSelected && someExcluded;
};

export type TSelectedLayoutEntry =
  | { type: 'FIELD'; id: string }
  | { type: 'GROUP'; id: string; items: { id: string }[] };

export interface SelectedCustomFieldGroups {
  workspaceId: string;
  fieldsLayout: TSelectedLayoutEntry[];
}

export type TImportTarget = { type: 'SITE' } | { type: 'GROUP'; groupId: string };

export interface ImportCustomFieldGroupsSelection {
  target: TImportTarget;
  sources: SelectedCustomFieldGroups[];
}

const toImportTarget = (targetGroupId?: string): TImportTarget =>
  targetGroupId ? { type: 'GROUP', groupId: targetGroupId } : { type: 'SITE' };

type TWorkspaceSelection = {
  fieldIds: string[];
  fieldsLayout: TSelectedLayoutEntry[];
};

type TSelectionByWorkspace = Record<string, TWorkspaceSelection>;

export interface ImportCustomFieldGroupsProps {
  accountOptions: TSelectOption[];
  siteOptions: TSiteOption[];
  layout: TLayoutEntry[];
  existingCustomFields: TTargetCustomField[];
  currentWorkspaceId: string;
  targetGroupId?: string;
  onSiteSelected: (workspaceId: string) => void;
  onSelectionChange: (selection: ImportCustomFieldGroupsSelection) => void;
}

const formatFieldsLayout = (
  layout: TLayoutEntry[],
  excludedFields: TExcludedFields,
  selectedFieldIds: string[],
  target: TImportTarget,
): TSelectedLayoutEntry[] =>
  layout.flatMap((entry): TSelectedLayoutEntry[] => {
    if (!isGroup(entry)) {
      return selectedFieldIds.includes(entry.id) ? [{ type: 'FIELD', id: entry.id }] : [];
    }

    const selectedGroupFieldIds = getSelectableFieldIds(entry.items, excludedFields).filter(
      (fieldId) => selectedFieldIds.includes(fieldId),
    );

    if (selectedGroupFieldIds.length === 0) {
      return [];
    }

    if (target.type === 'GROUP' || !entry.id) {
      return selectedGroupFieldIds.map((fieldId) => ({ type: 'FIELD', id: fieldId }));
    }

    return [
      {
        type: 'GROUP',
        id: entry.id,
        items: selectedGroupFieldIds.map((fieldId) => ({ id: fieldId })),
      },
    ];
  });

const toSelectedCustomFieldGroups = (
  selectionByWorkspace: TSelectionByWorkspace,
): SelectedCustomFieldGroups[] =>
  Object.entries(selectionByWorkspace)
    .filter(([, selection]) => selection.fieldsLayout.length > 0)
    .map(([workspaceId, selection]) => ({ workspaceId, fieldsLayout: selection.fieldsLayout }));

const toSelectedFieldCounts = (
  selectionByWorkspace: TSelectionByWorkspace,
): Record<string, number> =>
  Object.fromEntries(
    Object.entries(selectionByWorkspace).map(([workspaceId, selection]) => [
      workspaceId,
      selection.fieldIds.length,
    ]),
  );

const ImportCustomFieldGroups = ({
  accountOptions,
  siteOptions,
  layout,
  existingCustomFields,
  currentWorkspaceId,
  targetGroupId,
  onSiteSelected,
  onSelectionChange,
}: ImportCustomFieldGroupsProps) => {
  const [selectedSite, setSelectedSite] = useState<TSiteOption>();
  const [selectionByWorkspace, setSelectionByWorkspace] = useState<TSelectionByWorkspace>({});
  const [currentSelectedAccount, setCurrentSelectedAccount] = useState<string>();
  const populatedLayout = layout.filter(hasFields);
  const layoutFields = populatedLayout.flatMap((entry) => (isGroup(entry) ? entry.items : [entry]));
  const excludedFields = getExcludedFields(
    layoutFields,
    existingCustomFields.map((field) => field.label),
  );
  const selectableFieldIds = getSelectableFieldIds(layoutFields, excludedFields);
  const importTarget = toImportTarget(targetGroupId);
  const selectedFieldIds = selectedSite
    ? selectionByWorkspace[selectedSite.value]?.fieldIds ?? []
    : [];
  const selectionExcludesFields = populatedLayout.some((entry) =>
    hasExcludedSelection(entry, selectableFieldIds, selectedFieldIds),
  );

  const updateSelection = (customFieldIds: string[]) => {
    if (!selectedSite) {
      return;
    }

    const nextSelection: TSelectionByWorkspace = {
      ...selectionByWorkspace,
      [selectedSite.value]: {
        fieldIds: customFieldIds,
        fieldsLayout: formatFieldsLayout(
          populatedLayout,
          excludedFields,
          customFieldIds,
          importTarget,
        ),
      },
    };

    setSelectionByWorkspace(nextSelection);
    onSelectionChange({
      target: importTarget,
      sources: toSelectedCustomFieldGroups(nextSelection),
    });
  };

  const selectSite = (site: TSiteOption) => {
    setSelectedSite(site);
    onSiteSelected(site.value);
  };

  const backToSiteSelection = (accountId: string) => {
    setSelectedSite(undefined);
    setCurrentSelectedAccount(accountId);
  };

  const toggleField = (fieldId: string) => {
    if (selectedFieldIds.includes(fieldId)) {
      updateSelection(selectedFieldIds.filter((selectedId) => selectedId !== fieldId));

      return;
    }

    updateSelection([...selectedFieldIds, fieldId]);
  };

  const toggleGroup = (group: TLayoutGroup) => {
    const groupFieldIds = getSelectableFieldIds(group.items, excludedFields);
    const everySelected = groupFieldIds.every((fieldId) => selectedFieldIds.includes(fieldId));
    const withoutGroup = selectedFieldIds.filter((fieldId) => !groupFieldIds.includes(fieldId));

    updateSelection(everySelected ? withoutGroup : [...withoutGroup, ...groupFieldIds]);
  };

  const toggleAll = () => {
    const everySelected = selectableFieldIds.every((fieldId) => selectedFieldIds.includes(fieldId));

    updateSelection(everySelected ? [] : selectableFieldIds);
  };

  if (selectedSite) {
    return (
      <SelectImportGroups
        site={selectedSite}
        layout={populatedLayout}
        selectableFieldIds={selectableFieldIds}
        excludedFields={excludedFields}
        selectedFieldIds={selectedFieldIds}
        selectionExcludesFields={selectionExcludesFields}
        onToggleField={toggleField}
        onToggleGroup={toggleGroup}
        onToggleAll={toggleAll}
        onBackToSiteSelection={backToSiteSelection}
      />
    );
  }

  return (
    <SelectImportSite
      accountOptions={accountOptions}
      siteOptions={siteOptions}
      currentWorkspaceId={currentWorkspaceId}
      onSiteSelected={selectSite}
      currentSelectedAccount={currentSelectedAccount}
      selectedFieldCounts={toSelectedFieldCounts(selectionByWorkspace)}
    />
  );
};

export default ImportCustomFieldGroups;
